import { coreTypes } from "@core/core-types.di";
import { EXPAND_ALL, FieldsQuery } from "@core/data/dto/fields.dto";
import { HttpFailedRequestError } from "@core/data/infrastructures/http/errors/http-failed-request.error";
import { type Http } from "@core/data/infrastructures/http/http";
import { HttpConfig } from "@core/data/infrastructures/http/http-config";
import { HttpErrorCodeEnum } from "@core/data/infrastructures/http/http-error-response";
import { OrderMapper } from "@core/data/mappers/order.mapper";
import { CreateDocumentError } from "@core/domain/errors/create-document.error";
import { DocumentMaxSizeError } from "@core/domain/errors/document-max-size.error";
import { FallbackError } from "@core/domain/errors/fallback.error";
import { ValidationError } from "@core/domain/errors/validation.error";
import { CreateDocument } from "@core/domain/models/create-document.model";
import { Order } from "@core/domain/models/order.model";
import { Pagination } from "@core/domain/models/pagination";
import { Either } from "@core/domain/types/either";
import { CatalogueDto } from "@project/data/dto/catalogue.dto";
import { CreateProjectBody } from "@project/data/dto/create-project.body";
import { EditProjectBody } from "@project/data/dto/edit-project.body";
import {
    ProjectEmployeesDto,
    ProjectEmployeesQuery,
} from "@project/data/dto/project-employee/project-employees.dto";
import { ProjectDto } from "@project/data/dto/project.dto";
import { StatusDto } from "@project/data/dto/status.dto";
import { CatalogueMapper } from "@project/data/mappers/catalogue.mapper";
import { CreateProjectMapper } from "@project/data/mappers/create-project.mapper";
import { EditProjectMapper } from "@project/data/mappers/edit-project.mapper";
import { ProjectEmployeesMapper } from "@project/data/mappers/project-employees.mapper";
import { ProjectMapper } from "@project/data/mappers/project.mapper";
import { StatusMapper } from "@project/data/mappers/status.mapper";
import { Catalogue } from "@project/domain/models/catalogue.model";
import { CreateProject } from "@project/domain/models/create-project.model";
import { EditProject } from "@project/domain/models/edit-project.model";
import {
    ProjectEmployees,
    ProjectEmployeesSearchFilters,
} from "@project/domain/models/project-employees.model";
import {
    ProjectListDashboard,
    ProjectListDashboardAlert,
    ProjectListDashboardExportFormat,
    ProjectListDashboardHealthStatus,
    ProjectListDashboardProject,
    createEmptyProjectListDashboard,
} from "@project/domain/models/project-list-dashboard.model";
import { ProjectSummary } from "@project/domain/models/project-summary.model";
import { ProjectsSummaryTransformer } from "@project/domain/models/projects-summary.model";
import { Status } from "@project/domain/models/status.model";
import { plainToClass } from "class-transformer";
import { inject, injectable } from "inversify";
import { ProjectSearchFilters } from "../../domain/models/project-search-filters";
import { Project } from "../../domain/models/project.model";
import { TerritorialScope } from "../../domain/models/territorial-scope.model";
import {
    ProjectSummaryDto,
    ProjectsSummaryDto,
    ProjectsSummaryQuery,
    projectsSummaryOrderMap,
} from "../dto/project-summary.dto";
import { TerritorialScopeDto } from "../dto/territorial-scope.dto";
import { FinancialEntityTypeEnumMapper } from "../mappers/financial-entity/financial-entity-type-enum.mapper";
import { ProjectsSummaryMapper } from "../mappers/projects-summary.mapper";
import { StatusEnumMapper } from "../mappers/status-enum.mapper";
import { TerritorialScopeMapper } from "../mappers/territorial-scope.mapper";

@injectable()
export class ProjectDatasource {
    // eslint-disable-next-line max-params
    constructor(
        @inject(CatalogueMapper)
        private readonly catalogueMapper: CatalogueMapper,
        @inject(CreateProjectMapper)
        private readonly createProjectMapper: CreateProjectMapper,
        @inject(EditProjectMapper)
        private readonly editProjectMapper: EditProjectMapper,
        @inject(FinancialEntityTypeEnumMapper)
        private readonly financialEntityTypeEnumMapper: FinancialEntityTypeEnumMapper,
        @inject(coreTypes.infrastructure.Http)
        private readonly http: Http,
        @inject(OrderMapper) private readonly orderMapper: OrderMapper,
        @inject(ProjectEmployeesMapper)
        private readonly projectEmployeesMapper: ProjectEmployeesMapper,
        @inject(ProjectMapper)
        private readonly projectMapper: ProjectMapper,
        @inject(ProjectsSummaryMapper)
        private readonly projectsSummaryMapper: ProjectsSummaryMapper,
        @inject(StatusEnumMapper)
        private readonly statusEnumMapper: StatusEnumMapper,
        @inject(StatusMapper)
        private readonly statusMapper: StatusMapper,
        @inject(TerritorialScopeMapper)
        private readonly territorialScopeMapper: TerritorialScopeMapper,
    ) {}

    private normalizeProjectListDashboard(
        dashboard: Partial<ProjectListDashboard> | null | undefined,
    ): ProjectListDashboard {
        const emptyDashboard = createEmptyProjectListDashboard();
        const rawDashboard = dashboard as
            | Record<string, unknown>
            | null
            | undefined;
        const summary =
            (dashboard?.summary as Record<string, unknown> | undefined) ??
            (rawDashboard?.summary as Record<string, unknown> | undefined);
        const statusOverview =
            (dashboard?.statusOverview as unknown[] | undefined) ??
            (rawDashboard?.status_overview as unknown[] | undefined);
        const alerts =
            (dashboard?.alerts as Record<string, unknown> | undefined) ??
            (rawDashboard?.alerts as Record<string, unknown> | undefined);

        return {
            summary: {
                totalProjects:
                    typeof summary?.totalProjects === "number"
                        ? summary.totalProjects
                        : typeof summary?.total_projects === "number"
                          ? summary.total_projects
                          : emptyDashboard.summary.totalProjects,
                activeProjects:
                    typeof summary?.activeProjects === "number"
                        ? summary.activeProjects
                        : typeof summary?.active_projects === "number"
                          ? summary.active_projects
                          : emptyDashboard.summary.activeProjects,
                finalizedProjects:
                    typeof summary?.finalizedProjects === "number"
                        ? summary.finalizedProjects
                        : typeof summary?.finalized_projects === "number"
                          ? summary.finalized_projects
                          : emptyDashboard.summary.finalizedProjects,
                projectsWithAlerts:
                    typeof summary?.projectsWithAlerts === "number"
                        ? summary.projectsWithAlerts
                        : typeof summary?.projects_with_alerts === "number"
                          ? summary.projects_with_alerts
                          : emptyDashboard.summary.projectsWithAlerts,
                overdueActivities:
                    typeof summary?.overdueActivities === "number"
                        ? summary.overdueActivities
                        : typeof summary?.overdue_activities === "number"
                          ? summary.overdue_activities
                          : emptyDashboard.summary.overdueActivities,
                upcomingActivities:
                    typeof summary?.upcomingActivities === "number"
                        ? summary.upcomingActivities
                        : typeof summary?.upcoming_activities === "number"
                          ? summary.upcoming_activities
                          : emptyDashboard.summary.upcomingActivities,
                atRiskIndicators:
                    typeof summary?.atRiskIndicators === "number"
                        ? summary.atRiskIndicators
                        : typeof summary?.at_risk_indicators === "number"
                          ? summary.at_risk_indicators
                          : emptyDashboard.summary.atRiskIndicators,
                monitoringOverdueIndicators:
                    typeof summary?.monitoringOverdueIndicators === "number"
                        ? summary.monitoringOverdueIndicators
                        : typeof summary?.monitoring_overdue_indicators ===
                            "number"
                          ? summary.monitoring_overdue_indicators
                          : emptyDashboard.summary.monitoringOverdueIndicators,
            },
            statusOverview: Array.isArray(statusOverview)
                ? statusOverview.map((bucket) => {
                      const rawBucket = bucket as Record<string, unknown>;

                      return {
                          key:
                              (rawBucket.key as ProjectListDashboardHealthStatus) ??
                              "on_track",
                          label:
                              typeof rawBucket.label === "string"
                                  ? rawBucket.label
                                  : "",
                          count:
                              typeof rawBucket.count === "number"
                                  ? rawBucket.count
                                  : 0,
                      };
                  })
                : emptyDashboard.statusOverview,
            alerts: {
                overdueActivities: this.normalizeProjectListDashboardAlerts(
                    Array.isArray(alerts?.overdueActivities)
                        ? alerts.overdueActivities
                        : Array.isArray(alerts?.overdue_activities)
                          ? alerts.overdue_activities
                          : emptyDashboard.alerts.overdueActivities,
                ),
                upcomingActivities: this.normalizeProjectListDashboardAlerts(
                    Array.isArray(alerts?.upcomingActivities)
                        ? alerts.upcomingActivities
                        : Array.isArray(alerts?.upcoming_activities)
                          ? alerts.upcoming_activities
                          : emptyDashboard.alerts.upcomingActivities,
                ),
                atRiskIndicators: this.normalizeProjectListDashboardAlerts(
                    Array.isArray(alerts?.atRiskIndicators)
                        ? alerts.atRiskIndicators
                        : Array.isArray(alerts?.at_risk_indicators)
                          ? alerts.at_risk_indicators
                          : emptyDashboard.alerts.atRiskIndicators,
                ),
                monitoringOverdueIndicators: Array.isArray(
                    alerts?.monitoringOverdueIndicators,
                )
                    ? this.normalizeProjectListDashboardAlerts(
                          alerts.monitoringOverdueIndicators,
                      )
                    : Array.isArray(alerts?.monitoring_overdue_indicators)
                      ? this.normalizeProjectListDashboardAlerts(
                            alerts.monitoring_overdue_indicators,
                        )
                      : emptyDashboard.alerts.monitoringOverdueIndicators,
            },
            projects: Array.isArray(dashboard?.projects)
                ? dashboard.projects.map((project) =>
                      this.normalizeProjectListDashboardProject(project),
                  )
                : Array.isArray(rawDashboard?.projects)
                  ? rawDashboard.projects.map((project) =>
                        this.normalizeProjectListDashboardProject(project),
                    )
                  : emptyDashboard.projects,
        };
    }

    private normalizeProjectListDashboardAlerts(
        items: unknown[],
    ): ProjectListDashboardAlert[] {
        return items.map((item) => {
            const rawItem = item as Record<string, unknown> | null | undefined;

            return {
                projectId:
                    typeof rawItem?.projectId === "number"
                        ? rawItem.projectId
                        : typeof rawItem?.project_id === "number"
                          ? rawItem.project_id
                          : 0,
                projectName:
                    typeof rawItem?.projectName === "string"
                        ? rawItem.projectName
                        : typeof rawItem?.project_name === "string"
                          ? rawItem.project_name
                          : "",
                itemId:
                    typeof rawItem?.itemId === "number"
                        ? rawItem.itemId
                        : typeof rawItem?.item_id === "number"
                          ? rawItem.item_id
                          : 0,
                itemName:
                    typeof rawItem?.itemName === "string"
                        ? rawItem.itemName
                        : typeof rawItem?.item_name === "string"
                          ? rawItem.item_name
                          : "",
                itemType:
                    typeof rawItem?.itemType === "string"
                        ? rawItem.itemType
                        : typeof rawItem?.item_type === "string"
                          ? rawItem.item_type
                          : "",
                dueDate:
                    typeof rawItem?.dueDate === "string"
                        ? rawItem.dueDate
                        : typeof rawItem?.due_date === "string"
                          ? rawItem.due_date
                          : null,
                phase:
                    typeof rawItem?.phase === "string" ? rawItem.phase : null,
                priority:
                    typeof rawItem?.priority === "string"
                        ? rawItem.priority
                        : null,
                status:
                    typeof rawItem?.status === "string" ? rawItem.status : null,
            };
        });
    }

    private normalizeProjectListDashboardProject(
        project: Partial<ProjectListDashboardProject> | null | undefined,
    ): ProjectListDashboardProject {
        const rawProject = project as
            | Record<string, unknown>
            | null
            | undefined;

        return {
            id: typeof project?.id === "number" ? project.id : 0,
            name: project?.name ?? "",
            status: project?.status ?? "",
            statusLabel:
                project?.statusLabel ??
                (typeof rawProject?.status_label === "string"
                    ? rawProject.status_label
                    : ""),
            healthStatus:
                project?.healthStatus ??
                (rawProject?.health_status as
                    | ProjectListDashboardHealthStatus
                    | undefined) ??
                ("on_track" as ProjectListDashboardHealthStatus),
            healthLabel:
                project?.healthLabel ??
                (typeof rawProject?.health_label === "string"
                    ? rawProject.health_label
                    : "En progreso"),
            completionPercentage:
                typeof project?.completionPercentage === "number"
                    ? project.completionPercentage
                    : typeof rawProject?.completion_percentage === "number"
                      ? rawProject.completion_percentage
                      : 0,
            totalActivities:
                typeof project?.totalActivities === "number"
                    ? project.totalActivities
                    : typeof rawProject?.total_activities === "number"
                      ? rawProject.total_activities
                      : 0,
            completedActivities:
                typeof project?.completedActivities === "number"
                    ? project.completedActivities
                    : typeof rawProject?.completed_activities === "number"
                      ? rawProject.completed_activities
                      : 0,
            overdueActivitiesCount:
                typeof project?.overdueActivitiesCount === "number"
                    ? project.overdueActivitiesCount
                    : typeof rawProject?.overdue_activities_count === "number"
                      ? rawProject.overdue_activities_count
                      : 0,
            upcomingActivitiesCount:
                typeof project?.upcomingActivitiesCount === "number"
                    ? project.upcomingActivitiesCount
                    : typeof rawProject?.upcoming_activities_count === "number"
                      ? rawProject.upcoming_activities_count
                      : 0,
            atRiskIndicatorsCount:
                typeof project?.atRiskIndicatorsCount === "number"
                    ? project.atRiskIndicatorsCount
                    : typeof rawProject?.at_risk_indicators_count === "number"
                      ? rawProject.at_risk_indicators_count
                      : 0,
            offTrackIndicatorsCount:
                typeof project?.offTrackIndicatorsCount === "number"
                    ? project.offTrackIndicatorsCount
                    : typeof rawProject?.off_track_indicators_count === "number"
                      ? rawProject.off_track_indicators_count
                      : 0,
            monitoringOverdueIndicatorsCount:
                typeof project?.monitoringOverdueIndicatorsCount === "number"
                    ? project.monitoringOverdueIndicatorsCount
                    : typeof rawProject?.monitoring_overdue_indicators_count ===
                        "number"
                      ? rawProject.monitoring_overdue_indicators_count
                      : 0,
        };
    }

    private buildProjectsSummaryQuery(
        pagination?: Pagination,
        filters?: ProjectSearchFilters,
        order?: Order<ProjectSummary>,
        fields?: string,
    ): ProjectsSummaryQuery {
        const query: ProjectsSummaryQuery = {};
        const YEARS_LENGTH = 4;

        if (pagination) {
            query.limit = pagination.pageSize;
            query.offset = pagination.offset;
        }

        if (filters?.name) query.search = filters.name;
        if (filters?.catalogue) query.catalogue = filters.catalogue as never;
        if (filters?.phase) query.phase = filters.phase;
        if (filters?.priority) query.priority = filters.priority;
        if (filters?.statusProject)
            query.status = this.statusEnumMapper.mapToDto(
                filters.statusProject,
            );

        if (filters?.statusIn) {
            const statusIn = [] as string[];
            filters.statusIn.map((status) =>
                statusIn.push(this.statusEnumMapper.mapToDto(status)),
            );
            query.status_in = statusIn.join(",");
        }

        if (filters?.financialEntityType)
            query.finantial_entity_type =
                this.financialEntityTypeEnumMapper.mapToDto(
                    filters.financialEntityType,
                );
        if (filters?.entityIds) query.entity = filters.entityIds.join(",");
        if (filters?.activeFromDate)
            query.active_from_date = filters.activeFromDate.toISODate();
        if (filters?.activeToDate)
            query.active_to_date = filters.activeToDate.toISODate();
        if (filters?.years)
            query.years = filters.years
                .map((year) => year.toISODate()?.slice(0, YEARS_LENGTH))
                .join(",");
        if (filters?.materials) query.materials = filters.materials.join(",");

        if (filters?.collectiveIds)
            query.collectives = filters.collectiveIds.join(",");

        if (filters?.territorialScopeIds)
            query.territorial_scope = filters.territorialScopeIds.join(",");

        if (filters?.isActive) query.is_active = filters.isActive;

        if (filters?.continuity) query.continuity = filters.continuity;

        if (fields) query.fields = fields;

        if (order) {
            const orderQuery = this.orderMapper.mapToDto<
                ProjectSummary,
                ProjectSummaryDto
            >(order.field, order.direction, projectsSummaryOrderMap);

            if (orderQuery) {
                query.ordering = orderQuery;
            }
        }

        return query;
    }

    async create(
        entityId: number,
        newProject: CreateProject,
    ): Promise<Either<FallbackError | ValidationError, Project>> {
        const createdProjectDto = this.createProjectMapper.mapToDto(
            entityId,
            newProject,
        );

        const createProjectResult = await this.http.post<
            ProjectDto,
            CreateProjectBody
        >("/projects/", createdProjectDto);

        return createProjectResult
            .mapLeft((error) => {
                if (
                    error instanceof HttpFailedRequestError &&
                    error.errorCode === HttpErrorCodeEnum.GenericError
                ) {
                    return new ValidationError(error.data);
                }

                return new FallbackError();
            })
            .flatMap((response) => {
                const createdProject = this.projectMapper.map(
                    plainToClass(ProjectDto, response.data),
                );

                if (!createdProject) return Either.Left(new FallbackError());

                return Either.Right(createdProject);
            });
    }

    async fetchBy(
        pagination: Pagination,
        filters?: ProjectSearchFilters,
        order?: Order<ProjectSummary>,
        fields?: string,
    ): Promise<Either<FallbackError, ProjectsSummaryTransformer>> {
        const query = this.buildProjectsSummaryQuery(
            pagination,
            filters,
            order,
            fields,
        );

        const projectsResult = await this.http.get<ProjectsSummaryDto>(
            "/projects/",
            {
                query,
            },
        );

        return projectsResult
            .map((response) =>
                this.projectsSummaryMapper.mapToTransformer(
                    plainToClass(ProjectsSummaryDto, response.data),
                ),
            )
            .mapLeft(() => new FallbackError());
    }

    async fetchDashboard(
        filters?: ProjectSearchFilters,
    ): Promise<Either<FallbackError, ProjectListDashboard>> {
        const responseResult = await this.http.get<ProjectListDashboard>(
            "/projects/dashboard/",
            {
                query: this.buildProjectsSummaryQuery(undefined, filters),
            },
        );

        return responseResult
            .mapLeft(() => new FallbackError())
            .map((response) =>
                this.normalizeProjectListDashboard(
                    response.data as Partial<ProjectListDashboard>,
                ),
            );
    }

    async exportDashboard(
        format: ProjectListDashboardExportFormat,
        filters?: ProjectSearchFilters,
    ): Promise<Either<FallbackError, Blob>> {
        const exportPathByFormat: Record<
            ProjectListDashboardExportFormat,
            string
        > = {
            csv: "/projects/dashboard-export-csv/",
            xlsx: "/projects/dashboard-export-xlsx/",
            pdf: "/projects/dashboard-export-pdf/",
        };

        const responseResult = await this.http.get<Blob>(
            exportPathByFormat[format],
            {
                query: this.buildProjectsSummaryQuery(undefined, filters),
                responseType: "blob",
            },
        );

        return responseResult
            .mapLeft(() => new FallbackError())
            .map((response) => response.data as Blob);
    }

    async fetchAllCatalogues(): Promise<Either<FallbackError, Catalogue[]>> {
        const responseResult = await this.http.get<CatalogueDto[]>(
            "/projects/catalogue/",
        );

        return responseResult
            .mapLeft(() => new FallbackError())
            .map((response) =>
                response.data.mapNotNull((catalogueDto) =>
                    this.catalogueMapper.map(
                        plainToClass(CatalogueDto, catalogueDto),
                    ),
                ),
            );
    }

    async fetchAllStatus(): Promise<Either<FallbackError, Status[]>> {
        const responseResult =
            await this.http.get<StatusDto[]>("/projects/status/");

        return responseResult
            .mapLeft(() => new FallbackError())
            .map((response) =>
                response.data.mapNotNull((statusDto) =>
                    this.statusMapper.map(plainToClass(StatusDto, statusDto)),
                ),
            );
    }

    async fetchAllTerritorialScopes(): Promise<
        Either<FallbackError, TerritorialScope[]>
    > {
        const responseResult = await this.http.get<TerritorialScopeDto[]>(
            "/projects/territorial_scope/",
        );

        return responseResult
            .mapLeft(() => new FallbackError())
            .map((response) =>
                response.data.mapNotNull((typeDto) =>
                    this.territorialScopeMapper.map(
                        plainToClass(TerritorialScopeDto, typeDto),
                    ),
                ),
            );
    }

    async fetchAllProjectEmployeesBy(
        filters?: ProjectEmployeesSearchFilters,
    ): Promise<Either<FallbackError, ProjectEmployees>> {
        const query: ProjectEmployeesQuery = {};

        if (filters?.projectId) {
            query.project = filters.projectId;
        }
        const projectEmployeesResult = await this.http.get<ProjectEmployeesDto>(
            "/projects_employees/",
            {
                query,
            },
        );

        return projectEmployeesResult
            .mapLeft(() => new FallbackError())
            .map((response) =>
                this.projectEmployeesMapper.map(
                    plainToClass(ProjectEmployeesDto, response.data),
                ),
            );
    }

    async fetchById(
        projectId: number,
    ): Promise<Either<FallbackError, Project>> {
        const query: FieldsQuery = {
            expand: EXPAND_ALL,
        };

        const projectResult = await this.http.get<ProjectDto>(
            `/projects/${projectId}/`,
            {
                query,
            },
        );

        return projectResult
            .mapLeft(() => new FallbackError())
            .flatMap((response) => {
                const project = this.projectMapper.map(
                    plainToClass(ProjectDto, response.data),
                );

                if (!project) return Either.Left(new FallbackError());

                return Either.Right(project);
            });
    }

    async edit(
        entityId: number,
        projectToEdit: EditProject,
    ): Promise<Either<FallbackError | ValidationError, Project>> {
        const editProjectBody = this.editProjectMapper.mapToDto(
            entityId,
            projectToEdit,
        );

        const query: FieldsQuery = {
            expand: EXPAND_ALL,
        };

        const editProjectResult = await this.http.patch<
            ProjectDto,
            EditProjectBody
        >(`/projects/${projectToEdit.id}/update_nested/`, editProjectBody, {
            query,
        });

        return editProjectResult
            .mapLeft((error) => {
                if (
                    error instanceof HttpFailedRequestError &&
                    error.errorCode === HttpErrorCodeEnum.GenericError
                ) {
                    return new ValidationError(error.data);
                }

                return new FallbackError();
            })
            .flatMap((response) => {
                const project = this.projectMapper.map(
                    plainToClass(ProjectDto, response.data),
                );

                if (!project) return Either.Left(new FallbackError());

                return Either.Right(project);
            });
    }

    async importProjects(
        projectsImport: CreateDocument,
        entityId: number,
    ): Promise<Either<CreateDocumentError, boolean>> {
        const formData: FormData = new FormData();
        const config: HttpConfig = {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        };
        formData.append("csv_file", projectsImport.file);
        formData.append("entity", entityId.toString());

        const importedProjectsResult = await this.http.post<boolean>(
            "/projects/import_projects_from_csv/",
            formData,
            config,
        );

        return importedProjectsResult
            .mapLeft((error) => {
                if (
                    error instanceof HttpFailedRequestError &&
                    error.errorCode === HttpErrorCodeEnum.GenericError
                ) {
                    return new ValidationError(error.data);
                } else if (error instanceof HttpFailedRequestError) {
                    return new DocumentMaxSizeError();
                }

                return new FallbackError();
            })
            .flatMap(() => Either.Right(true));
    }

    async exportProjects(
        entityId: number,
    ): Promise<Either<FallbackError, Blob>> {
        const query: ProjectsSummaryQuery = {
            entity: entityId.toString(),
        };
        const projectsResult = await this.http.get<Blob>(
            `/projects/export_projects_csv/`,
            {
                responseType: "blob",
                query,
            },
        );

        return projectsResult
            .mapLeft(() => new FallbackError())
            .map((response) => response.data as Blob);
    }

    async delete(projectId: number): Promise<Either<FallbackError, true>> {
        const deleteResult = await this.http.delete(`/projects/${projectId}/`);

        return deleteResult
            .mapLeft(() => new FallbackError())
            .map(() => true as const);
    }
}
